/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { Template, TemplateType } from '@marageti/z4-sdk/lib/templates/Template';
import TemplateCard from './template-card';

type TemplatesViewProps = {
  onClickAdd: (typeOptions: TemplateType) => void,
  onClickEdit: (t: Template) => void,
  onClickRemove: (id: string, type: TemplateType) => void,
  templates: {
    [TemplateType.PartnershipsTemplate]: Template[],
    [TemplateType.CareTemplate]: Template[],
    [TemplateType.PersonalTemplate]: Template[],
    [TemplateType.TicketTemplate]: Template[],
  },
};

const TemplatesView = ({
  onClickAdd,
  onClickEdit,
  onClickRemove,
  templates,
}: TemplatesViewProps) => (
  <Container maxWidth="md">
    <Box
      sx={{
        py: { desktop: 4.5, mobile: 6.5 },
        mb: 20,
      }}
    >
      <Typography variant="h2" mb={3}>
        Templates
      </Typography>
      <Stack spacing={9}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" mb={0.5}>
              My Templates
            </Typography>
            <Typography color="primary.light">
              These are your personal templates to use within our messaging platform.
            </Typography>
          </Box>
          {templates[TemplateType.PersonalTemplate]?.length > 0 && (
            <Stack spacing={1.5}>
              {templates[TemplateType.PersonalTemplate].map((template) => (
                <TemplateCard
                  key={template.id}
                  onClickEdit={() => onClickEdit(template)}
                  onClickRemove={() => onClickRemove(template.id, template.templateType)}
                  {...template}
                />
              ))}
            </Stack>
          )}
          <Button
            onClick={() => onClickAdd(TemplateType.PersonalTemplate)}
            sx={{ maxWidth: { desktop: 'fit-content' } }}
            variant="outlined"
          >
            Add New Template
          </Button>
        </Stack>

        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" mb={0.5}>
              Partnerships Templates
            </Typography>
            <Typography color="primary.light">
              These are templates usable across your team within our messaging platform.
            </Typography>
          </Box>
          {templates[TemplateType.PartnershipsTemplate]?.length > 0 && (
            <Stack spacing={1.5}>
              {templates[TemplateType.PartnershipsTemplate].map((template) => (
                <TemplateCard
                  key={template.id}
                  onClickEdit={() => onClickEdit(template)}
                  onClickRemove={() => onClickRemove(template.id, template.templateType)}
                  {...template}
                />
              ))}
            </Stack>
          )}
          <Button
            onClick={() => onClickAdd(TemplateType.PartnershipsTemplate)}
            sx={{ maxWidth: { desktop: 'fit-content' } }}
            variant="outlined"
          >
            Add New Team Template
          </Button>
        </Stack>

        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" mb={0.5}>
              Care Templates
            </Typography>
            <Typography color="primary.light">
              These are templates usable across your team within our messaging platform.
            </Typography>
          </Box>
          {templates[TemplateType.CareTemplate]?.length > 0 && (
            <Stack spacing={1.5}>
              {templates[TemplateType.CareTemplate].map((template) => (
                <TemplateCard
                  key={template.id}
                  onClickEdit={() => onClickEdit(template)}
                  onClickRemove={() => onClickRemove(template.id, template.templateType)}
                  {...template}
                />
              ))}
            </Stack>
          )}
          <Button
            onClick={() => onClickAdd(TemplateType.CareTemplate)}
            sx={{ maxWidth: { desktop: 'fit-content' } }}
            variant="outlined"
          >
            Add New Team Template
          </Button>
        </Stack>

        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" mb={0.5}>
              Ticket Templates
            </Typography>
            <Typography color="primary.light">
              These are templates usable for ticket descriptions.
            </Typography>
          </Box>
          {templates[TemplateType.TicketTemplate]?.length > 0 && (
            <Stack spacing={1.5}>
              {templates[TemplateType.TicketTemplate].map((template) => (
                <TemplateCard
                  key={template.id}
                  onClickEdit={() => onClickEdit(template)}
                  onClickRemove={() => onClickRemove(template.id, template.templateType)}
                  {...template}
                />
              ))}
            </Stack>
          )}
          <Button
            onClick={() => onClickAdd(TemplateType.TicketTemplate)}
            sx={{ maxWidth: { desktop: 'fit-content' } }}
            variant="outlined"
          >
            Add New Ticket Template
          </Button>
        </Stack>
      </Stack>
    </Box>
  </Container>
);

export default TemplatesView;
